var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiRequest from 'root/src/client/logic/api/thunks/apiRequest';
import { GET_USER_NOTIFICATION_SETTINGS, SET_USER_NOTIFICATION_SETTINGS } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { realTimeKey, dailyKey, weeklyKey } from 'root/src/shared/constants/keys';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import { dareDropClient } from 'root/src/client/v2/common/clients/dareDropClient';
import { globalQueryClient } from 'root/src/client/v2/common/clients/queryClient';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { useAutoSave } from 'root/src/client/v2/common/hooks/useAutoSave';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const OPTIONS = [
    { value: realTimeKey, label: 'Real-time' },
    { value: dailyKey, label: 'Daily' },
    { value: weeklyKey, label: 'Weekly' },
];
export const NotificationSettingsSection = () => {
    const dispatch = useAppDispatch();
    const { authentication } = useAuthentication();
    const query = useQuery({
        queryKey: [GET_USER_NOTIFICATION_SETTINGS],
        queryFn: () => dareDropClient({ endpointId: GET_USER_NOTIFICATION_SETTINGS }),
        enabled: !!authentication,
    }, globalQueryClient);
    const [emailAlerts, setEmailAlerts] = useState(realTimeKey);
    const { saving, saved, failed, save, scheduleSave } = useAutoSave({
        onSave: () => __awaiter(void 0, void 0, void 0, function* () {
            const result = yield dispatch(apiRequest(SET_USER_NOTIFICATION_SETTINGS, { emailAlerts }));
            if (result === null || result === void 0 ? void 0 : result.error) {
                throw result.error;
            }
            globalQueryClient.invalidateQueries({ queryKey: [GET_USER_NOTIFICATION_SETTINGS] });
        }),
        loadingMessage: 'Saving notification settings...',
        successMessage: 'Notification settings saved',
        errorMessage: 'Could not save notification settings. Please try again.',
    });
    // Seed from the saved settings; does not trigger auto-save.
    useEffect(() => {
        const data = query.data;
        if (data === null || data === void 0 ? void 0 : data.emailAlerts) {
            setEmailAlerts(data.emailAlerts);
        }
    }, [query.data]);
    const selectOption = (value) => {
        setEmailAlerts(value);
        scheduleSave();
    };
    if (query.isLoading) {
        return (<div className="space-y-3">
				<Skeleton className="h-5 w-44"/>
				<Skeleton className="h-9 w-56"/>
			</div>);
    }
    return (<div>
			<h3 className="mb-1 text-sm font-semibold">Notification Settings</h3>
			<p className="mb-4 text-xs text-muted-foreground">How often we email you when clips are delivered. Changes save automatically.</p>
			<span className="mb-2 block text-[11px] font-medium text-muted-foreground">Clip delivered alert frequency</span>
			<div className="inline-flex rounded-lg border border-solid border-border/50 bg-secondary/20 p-0.5">
				{OPTIONS.map(option => (<button key={option.value} type="button" onClick={() => selectOption(option.value)} className={cn('rounded-md px-4 py-1.5 text-[11px] font-medium transition-colors', emailAlerts === option.value ? 'bg-accent/15 text-accent' : 'text-muted-foreground hover:text-foreground')}>
						{option.label}
					</button>))}
			</div>
			<div className="mt-5 flex justify-end">
				<Button size="sm" variant="brand" loading={saving} success={saved} error={failed} onClick={save} className="h-9 min-w-[110px] rounded-lg text-xs">
					Save
				</Button>
			</div>
		</div>);
};
